import React, { ChangeEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import MoreButton, { ButtonOptionsType } from '../../../FormElements/MoreButton';
import InputCheckBox from '../../../FormElements/InputCheckBox';

import {
  CheckboxDefinitionType,
  MoreButtonDefinitionType,
  OptionsType,
  ColumnDefinitionType,
} from '../genericTableTypes';
import Plataforma from "../../../../assets/image/Plataforma.svg";
import PlataformaDark from "../../../../assets/image/PlataformaDark.svg";
import Forno from "../../../../assets/image/Forno.svg";
import FornoDark from "../../../../assets/image/FornoDark.svg";
import { ReactComponent as ClockIcon } from "../../../../assets/image/Clock.svg";
import { ReactComponent as DownloadIcon } from "../../../../assets/image/Download.svg";
import { ReactComponent as CheckIcon } from "../../../../assets/image/Resolvido 2.svg";
import { ReactComponent as Icon24h } from "../../../../assets/image/24h.svg";
import { ReactComponent as Icon48h } from "../../../../assets/image/48h.svg";
import { ReactComponent as Icon72h } from "../../../../assets/image/72+.svg";
import { ReactComponent as TrashColorRed } from "../../../../assets/image/TrashColorRed.svg";
import { ReactComponent as EditColorBlue } from "../../../../assets/image/EditColorBlue.svg";

import * as Styled from './style';

import { RootState } from '../../../../store';
import { useSelector, useDispatch } from 'react-redux';
import { updateTempRecipeOrder } from '../../../../store/menus/menuSimpleSlice';

type TableBodyProps<T, K extends keyof T> = {
  id?: string;
  typeTutorial?: string;
  data: T[];
  theme?: string;
  columns: ColumnDefinitionType<T, K>[];
  moreButton?: MoreButtonDefinitionType;
  equipmentSpecificOptions?: OptionsType[];
  additionalOptionsPosition?: number;
  shouldApplyOptions?: (equipType: string | undefined) => boolean;
  checkbox?: CheckboxDefinitionType;
  onRowClicked?: (id: number) => void;
  showMobileHeaders?: boolean;
  isReording?: boolean;
  onReorder?: (newOrder: any[]) => void;
  clickUpdate?: (id: number, typeOptions?: string) => void;
  clickType?: (type: string) => void;
  className?: string;
};

const getOptionsList = (
  id: number,
  options: OptionsType[],
  equipmentSpecificOptions?: OptionsType[],
  equipType?: string,
  position: number = 1,
  shouldApplyOptions?: (equipType: string | undefined) => boolean
): ButtonOptionsType[] => {
  const updatedOptions =
    shouldApplyOptions?.(equipType) && equipmentSpecificOptions
      ? [
        ...options.slice(0, position),
        ...equipmentSpecificOptions.map((option) => ({
          value: option.value,
          action: () => option.action(id),
          isTooltip: option.isTooltip,
          tooltipText: option.tooltipText
        })),
        ...options.slice(position),
      ]
      : options;

  return updatedOptions.map((option) => ({
    value: option.value,
    action: () => option.action(id),
    isTooltip: option.isTooltip,
    tooltipText: option.tooltipText
  }))
};

const TableBody = <T extends { id: number, equipTypeId?: string, legendColor?: string }, K extends keyof T>({
  id,
  typeTutorial,
  data,
  columns,
  theme,
  moreButton = { show: false, options: [] },
  equipmentSpecificOptions = [],
  additionalOptionsPosition = 1,
  shouldApplyOptions = () => false,
  checkbox = { show: false, action: () => { } },
  onRowClicked = () => { },
  showMobileHeaders = false,
  isReording = false,
  onReorder,
  clickUpdate = () => { },
  clickType = () => { },
  className
}: TableBodyProps<T, K>) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const showScrollbar = useSelector((state: RootState) => state.scroll.showScroll);
  const [listRecipeCmax, setListRecipeCmax] = useState<any[]>(data);

  const handleOnDragEnd = (result: { destination: any; source?: any; }) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const newListRecipe = [...listRecipeCmax];
    const [removed] = newListRecipe.splice(source.index, 1);
    newListRecipe.splice(destination.index, 0, removed);

    const updatedListRecipe = newListRecipe.map((recipe: any, index: number) => {
      return {
        ...recipe,
        recipePosition: index + 1
      }
    })

    setListRecipeCmax(updatedListRecipe)
    dispatch(updateTempRecipeOrder(updatedListRecipe))

    onReorder && onReorder([...updatedListRecipe])
  }

  const handleRowClicked = (object: any) => {
    if (!checkbox.show) {
      onRowClicked(object.id)
    } else {
      const input = document.querySelector(`#object-${object.id}`) as HTMLInputElement
      input.checked = !input.checked
      checkbox.action(object.id, input.checked)
    }
  }

  return (
    <>
      {!isReording ? (
        <Styled.TBody className='scrollable-content' hideScrolllbar={showScrollbar}>
          {data.map((object) => (
            <Styled.Row key={object.id} className={className} id={typeTutorial?.includes('store-equipment') ? 'joyride-store-equipment' : id}>
              {checkbox.show && (
                <Styled.Data
                  className='checkbox_table'
                  gridArea='more-button'
                  width='20px'
                >
                  <InputCheckBox
                    name={`object-${object.id}`}
                    id={`object-${object.id}`}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      checkbox.action(object.id, event.target.checked);
                    }}
                    isChecked={checkbox.selectedList?.includes(object.id)}
                  />
                </Styled.Data>
              )}
              {columns.map((column, index) => (
                column.key === 'recipeOnPlatform' ? (
                  <Styled.DataIcon
                    key={`cell-${index}`}
                    className={column.gridArea === 'title' ? 'title' : ''}
                    gridArea={column.gridArea}
                    width={column.width ? column.width : 'auto'}
                  >
                    <img src={theme === 'dark' ? FornoDark : Forno} alt='icon' width={'20px'} />
                    {object[column.key] && <img src={theme === 'dark' ? PlataformaDark : Plataforma} alt='icon' width={'23px'} />}
                  </Styled.DataIcon>
                ) : (column.key === 'download') ? (
                  <Styled.DataIcon
                    id='download'
                    key={`cell-${index}`}
                    className={column.gridArea === 'title' ? 'title' : ''}
                    gridArea={column.gridArea}
                    width={column.width ? column.width : 'auto'}
                  >
                    {object[column.key] === 'downloadAvailable' && (
                      <Styled.IconWrapper>
                        <DownloadIcon onClick={() => clickUpdate(object.id)} width={'20px'} height={'20px'} />
                        <Styled.Tooltip>{intl.formatMessage({ id: 'menus.Clique para atualizar' })}</Styled.Tooltip>
                      </Styled.IconWrapper>
                    )}
                    {object[column.key] === 'downloadPending' && (
                      <Styled.IconWrapper>
                        <ClockIcon width={'20px'} height={'20px'} />
                        <Styled.Tooltip>{intl.formatMessage({ id: 'home.Aceitação pendente' })}</Styled.Tooltip>
                      </Styled.IconWrapper>
                    )}
                    {object[column.key] === 'downloadUnavailable' && (
                      <Styled.IconWrapper>
                        <CheckIcon width={'20px'} height={'20px'} />
                        <Styled.Tooltip>{intl.formatMessage({ id: 'home.Atualizado' })}</Styled.Tooltip>
                      </Styled.IconWrapper>
                    )}
                  </Styled.DataIcon>
                ) : (column.key === 'alert') ? (
                  <Styled.DataIcon
                    id='alert'
                    key={`cell-${index}`}
                    className={column.gridArea === 'title' ? 'title' : ''}
                    gridArea={column.gridArea}
                    width={column.width ? column.width : 'auto'}
                  >
                    {Number(object[column.key]) < 24 && (
                      <Styled.IconWrapper>
                        <Icon24h width={'20px'} height={'20px'} />
                        <Styled.Tooltip>{intl.formatMessage({ id: 'equipment.Forno sem limpeza há' }) + ` ${object[column.key]}h`}</Styled.Tooltip>
                      </Styled.IconWrapper>
                    )}
                    {Number(object[column.key]) >= 24 && Number(object[column.key]) < 48 && (
                      <Styled.IconWrapper>
                        <Icon48h width={'20px'} height={'20px'} />
                        <Styled.Tooltip>{intl.formatMessage({ id: 'equipment.Forno sem limpeza há' }) + ` ${object[column.key]}h`}</Styled.Tooltip>
                      </Styled.IconWrapper>
                    )}
                    {Number(object[column.key]) >= 48 && (
                      <Styled.IconWrapper>
                        <Icon72h width={'20px'} height={'20px'} />
                        <Styled.Tooltip>{intl.formatMessage({ id: 'equipment.Forno sem limpeza há' }) + ` ${object[column.key]}h`}</Styled.Tooltip>
                      </Styled.IconWrapper>
                    )}
                  </Styled.DataIcon>
                ) : (column.key === 'delete') ? (
                  <Styled.DataIcon
                    id='delete'
                    key={`cell-${index}`}
                    className={column.gridArea === 'title' ? 'title' : ''}
                    gridArea={column.gridArea}
                    width={column.width ? column.width : 'auto'}
                  >
                    <Styled.IconWrapper>
                      <TrashColorRed onClick={() => (clickUpdate(object.id, 'delete'))} />
                    </Styled.IconWrapper>
                  </Styled.DataIcon>
                ) : (column.key === 'edit') ? (
                  <Styled.DataIcon
                    id='edit'
                    key={`cell-${index}`}
                    className={column.gridArea === 'title' ? 'title' : ''}
                    gridArea={column.gridArea}
                    width={column.width ? column.width : 'auto'}
                  >
                    <Styled.IconWrapper>
                      <EditColorBlue onClick={() => (clickUpdate(object.id, 'edit'))} />
                    </Styled.IconWrapper>
                  </Styled.DataIcon>
                ) : (column.key === 'conect') ? (
                  <Styled.Data
                    key={`cell-${index}`}
                    className={column.gridArea === 'title' ? 'title' : ''}
                    onClick={() => handleRowClicked(object)}
                    gridArea={column.gridArea}
                    width={column.width ? column.width : 'auto'}
                  >
                    {showMobileHeaders && !column.header.endsWith('.Nome') && !column.header.endsWith('.nome') && <span className='mobile-header'>{intl.formatMessage({ id: column.header })}:&nbsp;</span>}
                    {(intl.formatMessage({ id: object[column.key] ? "equipment.sim" : "equipment.não" }))}
                  </Styled.Data>
                ) : (
                  <Styled.Data
                    key={`cell-${index}`}
                    className={column.gridArea === 'title' ? 'title' : ''}
                    onClick={() => handleRowClicked(object)}
                    gridArea={column.gridArea}
                    width={column.width ? column.width : 'auto'}
                  >
                    {showMobileHeaders && !column.header.endsWith('.Nome') && !column.header.endsWith('.nome') && <span className='mobile-header'>{intl.formatMessage({ id: column.header })}:&nbsp;</span>}
                    {column.key === 'status' ? (
                      <>
                        <Styled.Ellipse status={String(object[column.key])} />
                        <Styled.StatusText>{object[column.key] === '-' ? '-' : (intl.formatMessage({ id: `equipment.${object[column.key]}` }))}</Styled.StatusText>
                      </>
                    ) : (column.key === 'statusOnOff') ? (
                      <>
                        <Styled.Ellipse status={String(object[column.key])} />
                      </>
                    ) : (column.key === 'errorMessage') ? (
                      <>
                        <Styled.Ellipse status={object.legendColor ?? '-'} />
                        <Styled.StatusTextMenssage>{object[column.key]}</Styled.StatusTextMenssage>
                      </>
                    ) : (
                      object[column.key]
                    )}
                  </Styled.Data>
                )
              ))}
              {moreButton.show && (
                <Styled.Data
                  className='more-icon'
                  gridArea='more-button'
                  width='15px'
                >
                  <div id={`${typeTutorial?.includes('edit-equipment') ? 'joyride-edit-equipment' : typeTutorial}`}>
                    <MoreButton
                      options={
                        getOptionsList(
                          object.id,
                          moreButton.options,
                          equipmentSpecificOptions,
                          object.equipTypeId,
                          additionalOptionsPosition,
                          shouldApplyOptions
                        )
                      }
                    />
                  </div>
                </Styled.Data>
              )}
            </Styled.Row>
          ))}
        </Styled.TBody >
      ) : (
        <DragDropContext onDragEnd={handleOnDragEnd} >
          <Droppable droppableId='tableRecipeCmax'>
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} >
                {listRecipeCmax.map((item, index) => (
                  <Draggable key={item.id} draggableId={item.id.toString()} index={index} >
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        <Styled.Row className={index % 2 === 0 ? '' : 'even'} >
                          {columns.map((column, index) => (
                            <Styled.Data
                              key={`cell-${index}`}
                              className={column.gridArea === 'title' ? 'title' : ''}
                              gridArea={column.gridArea}
                              width={column.width ? column.width : 'auto'}
                            >
                              {showMobileHeaders && !column.header.endsWith('.Nome') && !column.header.endsWith('.nome') && <span className='mobile-header'>{intl.formatMessage({ id: column.header })}:&nbsp;</span>}
                              {item[column.key]}
                            </Styled.Data>
                          ))}
                        </Styled.Row>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </>
  )
};

export default TableBody;
